import { type WebAudioAPISoundManager } from "@/audio";
import balladKickUrl from "@/assets/music-games/piano-studio-jr/drums/Ballad_Kick.mp3";
import balladRideUrl from "@/assets/music-games/piano-studio-jr/drums/Ballad_Ride.mp3";
import balladSnareUrl from "@/assets/music-games/piano-studio-jr/drums/Ballad_Snare.mp3";
import popHiHatClosedUrl from "@/assets/music-games/piano-studio-jr/drums/Pop_HiHat_Closed.mp3";
import popKickUrl from "@/assets/music-games/piano-studio-jr/drums/Pop_Kick.mp3";
import popSnareUrl from "@/assets/music-games/piano-studio-jr/drums/Pop_Snare.mp3";
import rockHiHatOpenUrl from "@/assets/music-games/piano-studio-jr/drums/Rock_HiHat_Open.mp3";
import rockKickUrl from "@/assets/music-games/piano-studio-jr/drums/Rock_Kick.mp3";
import rockSnareUrl from "@/assets/music-games/piano-studio-jr/drums/Rock_Snare.mp3";
import pianoC2Url from "@/assets/music-games/piano-studio-jr/piano/C2.mp3";
import pianoC3Url from "@/assets/music-games/piano-studio-jr/piano/C3.mp3";
import pianoC4Url from "@/assets/music-games/piano-studio-jr/piano/C4.mp3";
import pianoC5Url from "@/assets/music-games/piano-studio-jr/piano/C5.mp3";
import pianoC6Url from "@/assets/music-games/piano-studio-jr/piano/C6.mp3";
import pianoC7Url from "@/assets/music-games/piano-studio-jr/piano/C7.mp3";
import pianoC8Url from "@/assets/music-games/piano-studio-jr/piano/C8.mp3";
import {
  type PianoStudioJrDrumStyle,
  type PianoStudioJrPianoStyle,
} from "@/types/music_games";

// The usual names for each note, for when a note isn't part of a chord. Notes
// in a chord are named using `chordNoteName` instead.
export const NOTE_NAMES = [
  "C",
  "C♯",
  "D",
  "E♭",
  "E",
  "F",
  "F♯",
  "G",
  "A♭",
  "A",
  "B♭",
  "B",
] as const;

// Black keys can be named either way, depending on the chord
const BLACK_KEY_NAMES: Record<number, [string, string]> = {
  1: ["C♯", "D♭"],
  3: ["D♯", "E♭"],
  6: ["F♯", "G♭"],
  8: ["G♯", "A♭"],
  10: ["A♯", "B♭"],
};

// For lists of root notes, where both names should be shown
export function rootNoteMenuName(root: number) {
  return BLACK_KEY_NAMES[root]?.join(" / ") ?? NOTE_NAMES[root];
}

// These must match `PIANO_STUDIO_JR_CHORD_QUALITY_CHOICES` on the server
export const CHORD_INTERVALS: Record<string, number[]> = {
  Major: [0, 4, 7],
  Minor: [0, 3, 7],
  "Major 7th": [0, 4, 7, 11],
  "Minor 7th": [0, 3, 7, 10],
  "Dominant 7th": [0, 4, 7, 10],
  Sus2: [0, 2, 7],
  Sus4: [0, 5, 7],
  Diminished: [0, 3, 6],
  Augmented: [0, 4, 8],
};

// The scale degree of each note in the chord, which decides its letter name
const CHORD_DEGREES: Record<string, number[]> = {
  Major: [1, 3, 5],
  Minor: [1, 3, 5],
  "Major 7th": [1, 3, 5, 7],
  "Minor 7th": [1, 3, 5, 7],
  "Dominant 7th": [1, 3, 5, 7],
  Sus2: [1, 2, 5],
  Sus4: [1, 4, 5],
  Diminished: [1, 3, 5],
  Augmented: [1, 3, 5],
};

export const CHORD_QUALITIES = Object.keys(CHORD_INTERVALS);

// This must match `MAX_PROGRESSION_LENGTH` on the server
export const MAX_PROGRESSION_LENGTH = 64;

// Students unlock each level by mastering every chord in the previous one
export const CHORD_LEVELS = [
  { name: "Major & Minor", qualities: ["Major", "Minor"] },
  ...CHORD_QUALITIES.slice(2).map((quality) => ({
    name: quality,
    qualities: [quality],
  })),
];

const LETTERS = ["C", "D", "E", "F", "G", "A", "B"];
const LETTER_PITCH_CLASSES = [0, 2, 4, 5, 7, 9, 11];

const ACCIDENTALS: Record<number, string> = {
  [-2]: "♭♭",
  [-1]: "♭",
  0: "",
  1: "♯",
  2: "♯♯",
};

function spellChordFromRootName(
  root: number,
  rootName: string,
  quality: string,
) {
  const rootLetterIndex = LETTERS.indexOf(rootName[0]);

  return CHORD_INTERVALS[quality].map((interval, i) => {
    const letterIndex =
      (rootLetterIndex + CHORD_DEGREES[quality][i] - 1) % LETTERS.length;
    const pitchClass = (root + interval) % 12;
    const offset =
      ((pitchClass - LETTER_PITCH_CLASSES[letterIndex] + 18) % 12) - 6;

    return LETTERS[letterIndex] + ACCIDENTALS[offset];
  });
}

// 7th and suspended chords are taught as a major or minor chord with a note
// added or changed, so their root is named the same way as that chord's
const ROOT_NAMING_QUALITIES: Record<string, string> = {
  "Major 7th": "Major",
  "Minor 7th": "Minor",
  "Dominant 7th": "Major",
  Sus2: "Major",
  Sus4: "Major",
};

function totalAccidentals(names: string[]) {
  return names.join("").replace(/[^♭♯]/g, "").length;
}

// The name of a chord's root. A black-key root uses whichever of its names
// needs the fewest sharps and flats in the chord, the way musicians would
// write it (D♭ Major, but C♯ Minor). On a tie, the usual name is used.
export function chordRootName(root: number, quality: string) {
  const rootNames = BLACK_KEY_NAMES[root];

  if (rootNames === undefined) {
    return NOTE_NAMES[root];
  }

  const namingQuality = ROOT_NAMING_QUALITIES[quality] ?? quality;
  const [sharpName, flatName] = rootNames.map((rootName) =>
    totalAccidentals(spellChordFromRootName(root, rootName, namingQuality)),
  );

  if (sharpName === flatName) {
    return NOTE_NAMES[root];
  }

  return sharpName < flatName ? rootNames[0] : rootNames[1];
}

// The names of a chord's notes, in the order of its intervals. Each note gets
// the letter of its scale degree, so C Minor is C, E♭, G rather than C, D♯, G.
function chordSpelling(root: number, quality: string) {
  return spellChordFromRootName(root, chordRootName(root, quality), quality);
}

export function chordName(root: number, quality: string) {
  return `${chordRootName(root, quality)} ${quality}`;
}

// The name of a note, as part of the given chord if it's in it
export function chordNoteName(midi: number, root: number, quality: string) {
  const index = CHORD_INTERVALS[quality].findIndex(
    (interval) => (root + interval) % 12 === midi % 12,
  );

  return index === -1
    ? NOTE_NAMES[midi % 12]
    : chordSpelling(root, quality)[index];
}

export function chordMidi(
  root: number,
  quality: string,
  inversion = 0,
  anchor = 60,
) {
  const base = anchor + ((root - (anchor % 12) + 12) % 12);
  const notes = CHORD_INTERVALS[quality].map((interval) => base + interval);

  for (let i = 0; i < Math.min(inversion, notes.length - 1); i++) {
    notes[i] += 12;
  }

  return notes.sort((a, b) => a - b);
}

export const DRUM_STYLE_LABELS: Record<PianoStudioJrDrumStyle, string> = {
  none: "No drums",
  pop: "Pop",
  rock: "Rock",
  ballad: "Ballad",
};

export const PIANO_STYLE_LABELS: Record<PianoStudioJrPianoStyle, string> = {
  sustain: "Long, held chords",
  rhythm: "Rhythmic chords",
  arpeggio: "Rolling notes (arpeggio)",
};

// Song references are illustrative, not full transcriptions
export const PROGRESSION_PRESETS: {
  id: string;
  name: string;
  formula: string;
  description: string;
  example?: string;
  tempo?: number;
  drumStyle: PianoStudioJrDrumStyle;
  beats?: number[];
  chords: { root: number; quality: string }[];
}[] = [
  {
    id: "pop",
    name: "The Pop Progression",
    formula: "I – V – vi – IV",
    description: "Four chords heard in lots of pop music.",
    tempo: 72,
    beats: [2, 2, 2, 2],
    drumStyle: "pop",
    example: "Let It Be, The Beatles (one recurring passage)",
    chords: [
      { root: 0, quality: "Major" },
      { root: 7, quality: "Major" },
      { root: 9, quality: "Minor" },
      { root: 5, quality: "Major" },
    ],
  },
  {
    id: "fifties",
    name: "The Classic 50s",
    formula: "I – vi – IV – V – I",
    description:
      "A timeless sound for oldies and ballads, returning home to the tonic.",
    tempo: 118,
    beats: [8, 8, 4, 4, 8],
    drumStyle: "pop",
    example: "Stand by Me, Ben E. King (original recording key: A major)",
    chords: [
      { root: 9, quality: "Major" },
      { root: 6, quality: "Minor" },
      { root: 2, quality: "Major" },
      { root: 4, quality: "Major" },
      { root: 9, quality: "Major" },
    ],
  },
  {
    id: "journey",
    name: "The Four-Chord Journey",
    formula: "vi – IV – I – V",
    description: "Start with a minor chord for a dramatic feeling.",
    tempo: 98,
    drumStyle: "pop",
    example: "Africa, Toto (chorus progression, A major)",
    chords: [
      { root: 6, quality: "Minor" },
      { root: 2, quality: "Major" },
      { root: 9, quality: "Major" },
      { root: 4, quality: "Major" },
    ],
  },
  {
    id: "rock",
    name: "Rock Anthem",
    formula: "I – IV – V – IV",
    description: "Strong major chords that work with a rock beat.",
    drumStyle: "rock",
    chords: [
      { root: 0, quality: "Major" },
      { root: 5, quality: "Major" },
      { root: 7, quality: "Major" },
      { root: 5, quality: "Major" },
    ],
  },
  {
    id: "minor",
    name: "Minor Mood",
    formula: "i – III – VII – VI",
    description: "A powerful minor-key pattern.",
    tempo: 122,
    drumStyle: "pop",
    example: "Counting Stars, OneRepublic (recurring progression, C♯ minor)",
    chords: [
      { root: 1, quality: "Minor" },
      { root: 4, quality: "Major" },
      { root: 11, quality: "Major" },
      { root: 9, quality: "Major" },
    ],
  },
  {
    id: "bright",
    name: "Bright Beginning",
    formula: "I – IV – I – V",
    description: "A cheerful, simple four-chord pattern.",
    drumStyle: "pop",
    chords: [
      { root: 0, quality: "Major" },
      { root: 5, quality: "Major" },
      { root: 0, quality: "Major" },
      { root: 7, quality: "Major" },
    ],
  },
  {
    id: "soft",
    name: "Gentle Ballad",
    formula: "I – iii – IV – V",
    description: "A softer progression with a surprising minor chord.",
    tempo: 90,
    drumStyle: "ballad",
    chords: [
      { root: 0, quality: "Major" },
      { root: 4, quality: "Minor" },
      { root: 5, quality: "Major" },
      { root: 7, quality: "Major" },
    ],
  },
  {
    id: "descending",
    name: "Step Down",
    formula: "I – V – vi – iii – IV – I – IV – V",
    description:
      "The complete eight-chord cycle of Canon in D, returning to the beginning.",
    tempo: 80,
    drumStyle: "none",
    beats: [2, 2, 2, 2, 2, 2, 2, 2],
    example: "Canon in D, Pachelbel (complete repeating progression, D major)",
    chords: [
      { root: 2, quality: "Major" },
      { root: 9, quality: "Major" },
      { root: 11, quality: "Minor" },
      { root: 6, quality: "Minor" },
      { root: 7, quality: "Major" },
      { root: 2, quality: "Major" },
      { root: 7, quality: "Major" },
      { root: 9, quality: "Major" },
    ],
  },
  {
    id: "u2",
    name: "Big Open Sound",
    formula: "I – V – vi – IV",
    description: "The same four-chord relationship, in D major.",
    tempo: 110,
    drumStyle: "rock",
    example: "With or Without You, U2 (main repeated progression)",
    chords: [
      { root: 2, quality: "Major" },
      { root: 9, quality: "Major" },
      { root: 11, quality: "Minor" },
      { root: 7, quality: "Major" },
    ],
  },
  {
    id: "three",
    name: "Three-Chord Classic",
    formula: "I – IV – V",
    description:
      "Three essential chords for countless rock, folk, and blues songs.",
    tempo: 124,
    drumStyle: "pop",
    beats: [4, 4, 8],
    example: "Twist and Shout, The Beatles (simplified chord pattern, D major)",
    chords: [
      { root: 2, quality: "Major" },
      { root: 7, quality: "Major" },
      { root: 9, quality: "Major" },
    ],
  },
];

// Notes without their own sample are pitch-shifted from the nearest one
const PIANO_SAMPLE_URLS: Record<number, string> = {
  36: pianoC2Url,
  48: pianoC3Url,
  60: pianoC4Url,
  72: pianoC5Url,
  84: pianoC6Url,
  96: pianoC7Url,
  108: pianoC8Url,
};

type DrumKind = "kick" | "snare" | "hat";

const DRUM_SAMPLE_URLS: Record<
  Exclude<PianoStudioJrDrumStyle, "none">,
  Record<DrumKind, string>
> = {
  pop: { kick: popKickUrl, snare: popSnareUrl, hat: popHiHatClosedUrl },
  rock: { kick: rockKickUrl, snare: rockSnareUrl, hat: rockHiHatOpenUrl },
  ballad: { kick: balladKickUrl, snare: balladSnareUrl, hat: balladRideUrl },
};

export const AUDIO_URLS = [
  ...Object.values(PIANO_SAMPLE_URLS),
  ...Object.values(DRUM_SAMPLE_URLS).flatMap((kit) => Object.values(kit)),
];

export interface HeldNote {
  source: AudioBufferSourceNode;
  gain: GainNode;
}

export class PianoStudioJrAudio {
  soundManager: WebAudioAPISoundManager;
  sampleMidis: number[];
  scheduledNodes: AudioScheduledSourceNode[] = [];

  constructor(soundManager: WebAudioAPISoundManager) {
    this.soundManager = soundManager;
    this.sampleMidis = Object.keys(PIANO_SAMPLE_URLS).map(Number);
  }

  get context() {
    return this.soundManager.context;
  }

  async resume() {
    if (this.context.state === "suspended") {
      await this.context.resume();
    }
  }

  createNoteSource(midi: number) {
    const sampleMidi = this.sampleMidis.reduce((nearest, candidate) =>
      Math.abs(candidate - midi) < Math.abs(nearest - midi)
        ? candidate
        : nearest,
    );

    const source = this.context.createBufferSource();
    source.buffer = this.soundManager.bufferList[PIANO_SAMPLE_URLS[sampleMidi]];
    source.playbackRate.value = Math.pow(2, (midi - sampleMidi) / 12);

    const gain = this.context.createGain();
    source.connect(gain).connect(this.soundManager.masterGainNode);

    return { source, gain };
  }

  playNote(midi: number, duration: number, when = 0, volume = 0.28) {
    const { source, gain } = this.createNoteSource(midi);
    const start = Math.max(when, this.context.currentTime);
    const end = start + Math.max(0.08, duration);

    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.linearRampToValueAtTime(volume, start + 0.015);
    gain.gain.setValueAtTime(volume, Math.max(start + 0.02, end - 0.15));
    gain.gain.exponentialRampToValueAtTime(0.0001, end);
    source.start(start);
    source.stop(end + 0.02);

    return source;
  }

  // Used for notes that are part of a song, so that they can be stopped early
  scheduleNote(midi: number, duration: number, when: number) {
    this.trackScheduledNode(this.playNote(midi, duration, when, 0.22));
  }

  trackScheduledNode(node: AudioScheduledSourceNode) {
    this.scheduledNodes.push(node);

    node.addEventListener("ended", () => {
      this.scheduledNodes = this.scheduledNodes.filter((n) => n !== node);
    });
  }

  startHeldNote(midi: number): HeldNote {
    const { source, gain } = this.createNoteSource(midi);
    const now = this.context.currentTime;

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(0.28, now + 0.018);
    source.start(now);

    return { source, gain };
  }

  releaseHeldNote(note: HeldNote) {
    const now = this.context.currentTime;

    note.gain.gain.cancelScheduledValues(now);
    note.gain.gain.setTargetAtTime(0.0001, now, 0.055);
    note.source.stop(now + 0.35);
  }

  scheduleDrum(
    style: Exclude<PianoStudioJrDrumStyle, "none">,
    kind: DrumKind,
    when: number,
    volume: number,
  ) {
    // Mix levels chosen by ear for each kit
    if (kind === "kick") {
      volume *=
        style === "rock" ? 0.9 * 0.9 : style === "ballad" ? 0.8 * 0.9 : 0.9;
    }

    if (style === "rock") {
      volume *=
        kind === "hat"
          ? 0.72 * 0.85 * 0.85
          : kind === "snare"
            ? 0.85 * 0.85
            : 0.85;
    }

    const source = this.context.createBufferSource();
    source.buffer = this.soundManager.bufferList[DRUM_SAMPLE_URLS[style][kind]];

    const gain = this.context.createGain();
    gain.gain.value = Math.min(1.5, volume) * (kind === "hat" ? 0.55 : 0.85);

    source.connect(gain).connect(this.soundManager.masterGainNode);
    source.start(when);
    this.trackScheduledNode(source);
  }

  stopScheduled() {
    for (const node of this.scheduledNodes) {
      try {
        node.stop();
      } catch {
        // The node may have never started
      }
    }

    this.scheduledNodes = [];
  }
}
